import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Entity } from '../../entity/entity';
import { EntitySaveResponse } from '../../entity/entity-save-response';
import { EntityLineService } from '../../services/entity-line-service';
import { FileUtils } from '../../utils/file-utils';
import { StringUtils } from '../../utils/string-utils';

@Component({
  selector: 'app-bulk-upload-dialog',
  templateUrl: './bulk-upload-dialog.component.html',
  styleUrls: ['./bulk-upload-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, MatProgressSpinner, MatDialogActions],
})
export class BulkUploadDialogComponent {
  private readonly entityLineService = inject(EntityLineService);
  dialogRef = inject<MatDialogRef<BulkUploadDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);

  entity!: Entity;
  readonly fileBytes = signal<Uint8Array | undefined>(undefined);
  fileName: string | undefined;
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(false);
  isDragOver = false;
  readonly resultsShown = signal<boolean>(false);
  readonly successCount = signal<number>(0);
  readonly totalCount = signal<number>(0);
  readonly infoMessages = signal<string[]>([]);
  readonly importErrors = signal<string[]>([]);
  private lineCountBefore: number;
  private readonly uploadResponse = signal<EntitySaveResponse | null>(null);
  private onUploadComplete: ((response: EntitySaveResponse) => void) | undefined;

  constructor() {
    this.entity = this.data.entity as Entity;
    this.lineCountBefore = this.entity.lines ? this.entity.lines.length : 0;
    this.onUploadComplete = this.data.onUploadComplete;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if ((event.dataTransfer?.files?.length ?? 0) > 0) {
      this.processFile(event.dataTransfer!.files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if ((input.files?.length ?? 0) > 0) {
      this.processFile(input.files![0]);
      input.value = '';
    }
  }

  private processFile(file: File): void {
    this.errorMessage.set(undefined!);
    this.fileBytes.set(undefined);
    this.fileName = file.name;
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      this.fileBytes.set(reader.result as unknown as Uint8Array);
    };
    reader.onerror = () => {
      this.errorMessage.set('Unable to read the file.');
    };
  }

  downloadTemplate(): void {
    this.errorMessage.set(undefined!);
    this.entityLineService.getImportTemplate().subscribe({
      next: (blob: Blob) => {
        FileUtils.downloadFile(blob, 'Import Quote Lines Template.xlsx');
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage.set(StringUtils.getErrorMessage(error));
      },
    });
  }

  upload(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.entityLineService.importLines(this.entity.id.entityId, this.entity.id.entityRevision, this.fileBytes()!).subscribe({
      next: (response: EntitySaveResponse) => {
        this.isBusy.set(false);
        const updated = response.updated;
        const lineCountAfter = updated.lines ? updated.lines.length : 0;
        this.successCount.set(lineCountAfter - this.lineCountBefore);
        const messages = response.messages || [];
        this.importErrors.set(messages.filter((m) => m.startsWith('Row ')));
        this.infoMessages.set(messages.filter((m) => !m.startsWith('Row ')));
        this.totalCount.set(this.successCount() + this.importErrors().length);
        this.uploadResponse.set(response);
        this.resultsShown.set(true);
        this.onUploadComplete?.(response);
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage.set(StringUtils.getErrorMessage(error));
        this.isBusy.set(false);
      },
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  closeResults(): void {
    this.dialogRef.close(this.uploadResponse());
  }

  clearFile(): void {
    this.fileBytes.set(undefined);
    this.fileName = undefined;
    this.errorMessage.set(undefined!);
  }
}
